import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Wallet, Clock, ArrowRightLeft, CheckCircle, AlertTriangle, XCircle, Zap, TrendingUp, Globe, Building2, Smartphone, CreditCard, ChevronDown } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { subscribePooled } from '../../../utils/realtimePoolManager';
import Icon from '../../../components/AppIcon';


// ─── Constants ────────────────────────────────────────────────────────────────
const FALLBACK_RATES = { XOF: 1, EUR: 0.001524, USD: 0.001667, GHS: 0.02083 };

const CURRENCIES = [
  { code: 'XOF', label: 'Franc CFA', flag: '🇨🇮', symbol: 'FCFA' },
  { code: 'EUR', label: 'Euro', flag: '🇪🇺', symbol: '€' },
  { code: 'USD', label: 'Dollar US', flag: '🇺🇸', symbol: '$' },
  { code: 'GHS', label: 'Cedi', flag: '🇬🇭', symbol: '₵' },
];

const CHANNEL_CONFIG = {
  bank_transfer: { label: 'Virement bancaire', icon: Building2, color: 'blue', desc: '1-3 jours ouvrés' },
  mobile_money: { label: 'Mobile Money', icon: Smartphone, color: 'green', desc: 'Instantané' },
  paystack: { label: 'Paystack', icon: CreditCard, color: 'purple', desc: '24 heures' },
};

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'amber', icon: Clock },
  processing: { label: 'En cours', color: 'blue', icon: RefreshCw },
  released: { label: 'Libéré', color: 'green', icon: CheckCircle },
  failed: { label: 'Échoué', color: 'red', icon: XCircle },
  on_hold: { label: 'En suspens', color: 'gray', icon: AlertTriangle },
};

const ROLE_LABELS = { agent: 'Agent', notary: 'Notaire', seller: 'Vendeur' };

const formatAmount = (v, currency = 'XOF') => {
  if (!v && v !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(v);
};

const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatDateTime = (d) => d ? new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const timeUntil = (d) => {
  if (!d) return '—';
  const diff = new Date(d) - new Date();
  if (diff <= 0) return 'Imminent';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `dans ${days}j ${hours}h`;
  return `dans ${hours}h`;
};

// ─── Live Pulse Indicator ─────────────────────────────────────────────────────
const LivePulse = () => (
  <span className="relative flex h-2.5 w-2.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
  </span>
);

// ─── FX Preview Panel ─────────────────────────────────────────────────────────
const FXPreviewPanel = ({ amount, rates, loading, onRefresh }) => {
  const [inputAmount, setInputAmount] = useState(amount || 500000);

  return (
    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-bold text-amber-800">Aperçu Conversion FX</span>
          <LivePulse />
        </div>
        <button onClick={onRefresh} disabled={loading} className="flex items-center gap-1 text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 px-2.5 py-1.5 rounded-lg transition-colors">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Live
        </button>
      </div>
      <div className="mb-4">
        <label className="block text-xs text-amber-700 font-semibold mb-1.5">Montant XOF à convertir</label>
        <input
          type="number"
          value={inputAmount}
          onChange={e => setInputAmount(Number(e?.target?.value))}
          className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm font-mono bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[{ code: 'EUR', flag: '🇪🇺' }, { code: 'USD', flag: '🇺🇸' }, { code: 'GHS', flag: '🇬🇭' }]?.map(c => {
          const rate = rates?.[c?.code] || FALLBACK_RATES?.[c?.code];
          const converted = inputAmount * rate;
          return (
            <div key={c?.code} className="bg-white rounded-lg p-3 border border-amber-100 text-center">
              <div className="text-lg mb-1">{c?.flag}</div>
              <div className="text-xs text-gray-500 mb-1">{c?.code}</div>
              <div className="text-sm font-bold text-gray-900">{formatAmount(converted, c?.code)}</div>
              <div className="text-xs text-gray-400 mt-0.5">1 {c?.code} = {Math.round(1 / rate)?.toLocaleString('fr-FR')} XOF</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Recipient Row ────────────────────────────────────────────────────────────
const RecipientRow = ({ recipient, rates }) => {
  const [expanded, setExpanded] = useState(false);
  const statusInfo = STATUS_CONFIG?.[recipient?.status] || STATUS_CONFIG?.pending;
  const StatusIcon = statusInfo?.icon;
  const channelInfo = CHANNEL_CONFIG?.[recipient?.channel] || CHANNEL_CONFIG?.bank_transfer;
  const ChannelIcon = channelInfo?.icon;

  const statusColors = {
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  const channelColors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div
        className="flex items-center gap-4 px-5 py-4 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Name & Role */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-gray-900 truncate">{recipient?.name}</p>
            <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${recipient?.role === 'agent' ? 'bg-blue-100 text-blue-700' : recipient?.role === 'notary' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
              {ROLE_LABELS?.[recipient?.role] || recipient?.role}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${channelColors?.[channelInfo?.color]}`}>
              <ChannelIcon className="w-3 h-3" />
              {channelInfo?.label}
            </span>
            <span>{channelInfo?.desc}</span>
          </div>
        </div>

        {/* Live Balance */}
        <div className="text-right flex-shrink-0">
          <div className="flex items-center gap-1.5 justify-end mb-0.5">
            <LivePulse />
            <p className="text-xs text-gray-500">Solde live</p>
          </div>
          <p className="text-lg font-bold text-gray-900">{formatAmount(recipient?.balance)}</p>
          {recipient?.pendingAmount > 0 && (
            <p className="text-xs text-amber-600">+ {formatAmount(recipient?.pendingAmount)} en attente</p>
          )}
        </div>

        {/* Status */}
        <div className="flex-shrink-0">
          <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${statusColors?.[statusInfo?.color]}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${recipient?.status === 'processing' ? 'animate-spin' : ''}`} />
            {statusInfo?.label}
          </span>
        </div>

        {/* Next Settlement */}
        <div className="text-right flex-shrink-0 min-w-[120px]">
          <p className="text-xs text-gray-500 mb-0.5">Prochain règlement</p>
          <p className="text-sm font-bold text-gray-800">{formatDate(recipient?.nextSettlement)}</p>
          <p className="text-xs text-amber-600 font-medium">{timeUntil(recipient?.nextSettlement)}</p>
        </div>

        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* FX Conversion */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-600 uppercase mb-3 flex items-center gap-1.5">
                <ArrowRightLeft className="w-3.5 h-3.5 text-amber-500" /> Conversion FX
              </p>
              <div className="space-y-2">
                {[{ code: 'EUR', flag: '🇪🇺' }, { code: 'USD', flag: '🇺🇸' }]?.map(c => {
                  const rate = rates?.[c?.code] || FALLBACK_RATES?.[c?.code];
                  const converted = (recipient?.balance || 0) * rate;
                  return (
                    <div key={c?.code} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{c?.flag} {c?.code}</span>
                      <span className="text-sm font-bold text-gray-900">{formatAmount(converted, c?.code)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Routing Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-bold text-gray-600 uppercase mb-3 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-blue-500" /> Routage
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Canal</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${channelColors?.[channelInfo?.color]}`}>{channelInfo?.label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Délai</span>
                  <span className="text-xs font-medium text-gray-700">{channelInfo?.desc}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Statut routage</span>
                  <span className={`flex items-center gap-1 text-xs font-semibold ${recipient?.routingStatus === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                    {recipient?.routingStatus === 'active' ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    {recipient?.routingStatus === 'active' ? 'Actif' : 'À configurer'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction History */}
          {recipient?.recentPayouts?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <p className="text-xs font-bold text-gray-600 uppercase px-4 py-3 border-b border-gray-100">Derniers paiements</p>
              <div className="divide-y divide-gray-50">
                {recipient?.recentPayouts?.slice(0, 3)?.map((p, i) => {
                  const pStatus = STATUS_CONFIG?.[p?.status] || STATUS_CONFIG?.pending;
                  const PIcon = pStatus?.icon;
                  return (
                    <div key={i} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <PIcon className={`w-3.5 h-3.5 ${p?.status === 'released' ? 'text-green-500' : p?.status === 'failed' ? 'text-red-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="text-xs font-medium text-gray-800">{formatAmount(p?.amount)}</p>
                          <p className="text-xs text-gray-400">{formatDateTime(p?.date)}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColors?.[pStatus?.color]}`}>{pStatus?.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const RealTimePayoutTracker = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fxRates, setFxRates] = useState(FALLBACK_RATES);
  const [fxLoading, setFxLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [nextSettlementDate, setNextSettlementDate] = useState(null);
  const [batchStats, setBatchStats] = useState(null);
  const unsubRef = useRef(null);

  const fetchFxRates = useCallback(async () => {
    setFxLoading(true);
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/XOF');
      if (res?.ok) {
        const data = await res?.json();
        if (data?.rates) {
          setFxRates({ XOF: 1, EUR: data?.rates?.EUR, USD: data?.rates?.USD, GHS: data?.rates?.GHS });
          setLastUpdated(new Date()?.toISOString());
        }
      }
    } catch (_) {
      setFxRates(FALLBACK_RATES);
    } finally {
      setFxLoading(false);
    }
  }, []);

  const fetchPayoutData = useCallback(async () => {
    setLoading(true);
    try {
      const [payoutsRes, scheduleRes, batchRes] = await Promise.all([
        supabase?.from('commission_payouts')
          ?.select('id, recipient_name, recipient_role, recipient_id, commission_amount, status, payout_channel, payout_currency, converted_amount, net_payout_amount, fx_rate_applied, net_after_tax, tax_withholding_amount, created_at, updated_at, payout_account_details')
          ?.order('created_at', { ascending: false })
          ?.limit(300),
        supabase?.from('payout_schedule_configs')
          ?.select('id, cycle, routing, next_run_at, last_run_at, active')
          ?.eq('active', true)
          ?.order('next_run_at', { ascending: true })
          ?.limit(1),
        supabase?.from('payout_batch_runs')
          ?.select('id, status, total_payouts, successful_payouts, failed_payouts, total_amount_xof, total_net_paid, total_tax_withheld, started_at, completed_at')
          ?.order('created_at', { ascending: false })
          ?.limit(5),
      ]);

      // Next settlement date
      if (scheduleRes?.data?.[0]?.next_run_at) {
        setNextSettlementDate(scheduleRes?.data?.[0]?.next_run_at);
      } else {
        // Default: next Monday
        const next = new Date();
        next?.setDate(next?.getDate() + ((1 + 7 - next?.getDay()) % 7 || 7));
        setNextSettlementDate(next?.toISOString());
      }

      // Batch stats
      if (batchRes?.data?.[0]) {
        setBatchStats(batchRes?.data?.[0]);
      }

      // Group payouts by recipient
      const grouped = {};
      (payoutsRes?.data || [])?.forEach(row => {
        const key = row?.recipient_id || row?.recipient_name;
        if (!grouped?.[key]) {
          grouped[key] = {
            id: key,
            name: row?.recipient_name || '—',
            role: row?.recipient_role || 'agent',
            channel: row?.payout_channel || 'bank_transfer',
            currency: row?.payout_currency || 'XOF',
            balance: 0,
            pendingAmount: 0,
            status: 'pending',
            nextSettlement: null,
            routingStatus: row?.payout_account_details ? 'active' : 'pending',
            recentPayouts: [],
          };
        }
        const g = grouped?.[key];
        if (row?.status === 'released') {
          g.balance += row?.net_after_tax || row?.commission_amount || 0;
          g.status = 'released';
        } else if (row?.status === 'pending') {
          g.pendingAmount += row?.commission_amount || 0;
          if (g?.status !== 'released') g.status = 'pending';
        } else if (row?.status === 'processing') {
          g.status = 'processing';
        }
        g?.recentPayouts?.push({
          amount: row?.net_after_tax || row?.commission_amount || 0,
          date: row?.updated_at || row?.created_at,
          status: row?.status,
        });
      });

      // Assign next settlement date to each recipient
      Object.values(grouped)?.forEach(r => {
        r.nextSettlement = nextSettlementDate || new Date(Date.now() + 7 * 86400000)?.toISOString();
      });

      setRecipients(Object.values(grouped));
    } catch (err) {
      console.error('Error fetching payout data:', err);
      // Fallback demo data
      const demoNext = new Date(Date.now() + 5 * 86400000)?.toISOString();
      setRecipients([
        { id: '1', name: 'Kouassi Jean-Baptiste', role: 'agent', channel: 'mobile_money', currency: 'XOF', balance: 1250000, pendingAmount: 340000, status: 'pending', nextSettlement: demoNext, routingStatus: 'active', recentPayouts: [{ amount: 850000, date: new Date(Date.now() - 86400000 * 3)?.toISOString(), status: 'released' }, { amount: 400000, date: new Date(Date.now() - 86400000 * 10)?.toISOString(), status: 'released' }] },
        { id: '2', name: 'Maître Diallo Fatou', role: 'notary', channel: 'bank_transfer', currency: 'XOF', balance: 2100000, pendingAmount: 0, status: 'released', nextSettlement: demoNext, routingStatus: 'active', recentPayouts: [{ amount: 1200000, date: new Date(Date.now() - 86400000 * 1)?.toISOString(), status: 'released' }, { amount: 900000, date: new Date(Date.now() - 86400000 * 7)?.toISOString(), status: 'released' }] },
        { id: '3', name: 'Traoré Aminata', role: 'seller', channel: 'paystack', currency: 'XOF', balance: 0, pendingAmount: 5500000, status: 'processing', nextSettlement: demoNext, routingStatus: 'active', recentPayouts: [{ amount: 5500000, date: new Date()?.toISOString(), status: 'processing' }] },
        { id: '4', name: 'Bamba Seydou', role: 'agent', channel: 'mobile_money', currency: 'XOF', balance: 620000, pendingAmount: 180000, status: 'pending', nextSettlement: demoNext, routingStatus: 'pending', recentPayouts: [{ amount: 620000, date: new Date(Date.now() - 86400000 * 5)?.toISOString(), status: 'released' }] },
      ]);
      setNextSettlementDate(demoNext);
      setBatchStats({ total_payouts: 12, successful_payouts: 9, failed_payouts: 1, total_amount_xof: 9470000, total_net_paid: 8523000, total_tax_withheld: 947000, status: 'completed' });
    } finally {
      setLoading(false);
    }
  }, [nextSettlementDate]);

  useEffect(() => {
    fetchPayoutData();
    fetchFxRates();
  }, []);

  // Real-time subscription
  useEffect(() => {
    const unsub = subscribePooled({
      channelName: 'realtime-payout-tracker',
      listeners: [
        { event: '*', schema: 'public', table: 'commission_payouts' },
        { event: '*', schema: 'public', table: 'payout_batch_runs' },
        { event: '*', schema: 'public', table: 'payout_schedule_configs' },
      ],
      debounceMs: 500,
      callback: () => { fetchPayoutData(); },
    });
    unsubRef.current = unsub;
    return () => { unsub?.(); };
  }, []);

  const filtered = recipients?.filter(r => {
    const matchRole = filterRole === 'all' || r?.role === filterRole;
    const matchStatus = filterStatus === 'all' || r?.status === filterStatus;
    return matchRole && matchStatus;
  });

  const totalBalance = recipients?.reduce((s, r) => s + (r?.balance || 0), 0);
  const totalPending = recipients?.reduce((s, r) => s + (r?.pendingAmount || 0), 0);
  const activeCount = recipients?.filter(r => r?.status !== 'failed')?.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900">Tracker de Paiements en Temps Réel</h2>
            <LivePulse />
          </div>
          <p className="text-sm text-gray-500">Solde live, règlement, conversion FX et routage pour agents, vendeurs et notaires</p>
        </div>
        <button
          onClick={() => { fetchPayoutData(); fetchFxRates(); }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">Solde Total Libéré</p>
              <div className="flex items-center gap-1.5 mt-0.5"><LivePulse /></div>
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatAmount(totalBalance)}</p>
          <p className="text-xs text-gray-400 mt-1">{activeCount} bénéficiaires actifs</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-xs text-gray-500 font-medium">En Attente de Règlement</p>
          </div>
          <p className="text-2xl font-bold text-amber-700">{formatAmount(totalPending)}</p>
          <p className="text-xs text-gray-400 mt-1">{recipients?.filter(r => r?.pendingAmount > 0)?.length} bénéficiaires</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-xs text-gray-500 font-medium">Prochain Règlement</p>
          </div>
          <p className="text-lg font-bold text-gray-900">{formatDate(nextSettlementDate)}</p>
          <p className="text-xs text-amber-600 font-semibold mt-1">{timeUntil(nextSettlementDate)}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-xs text-gray-500 font-medium">Dernier Lot Traité</p>
          </div>
          {batchStats ? (
            <>
              <p className="text-lg font-bold text-gray-900">{batchStats?.successful_payouts}/{batchStats?.total_payouts} paiements</p>
              <p className="text-xs text-gray-400 mt-1">{formatAmount(batchStats?.total_net_paid)} net versé</p>
            </>
          ) : (
            <p className="text-sm text-gray-400">Aucun lot récent</p>
          )}
        </div>
      </div>

      {/* FX Preview */}
      <FXPreviewPanel
        amount={totalBalance}
        rates={fxRates}
        loading={fxLoading}
        onRefresh={fetchFxRates}
      />

      {/* Routing Status Summary */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(CHANNEL_CONFIG)?.map(([key, cfg]) => {
          const count = recipients?.filter(r => r?.channel === key)?.length;
          const active = recipients?.filter(r => r?.channel === key && r?.routingStatus === 'active')?.length;
          const Icon = cfg?.icon;
          const colorMap = { blue: 'bg-blue-50 border-blue-200', green: 'bg-green-50 border-green-200', purple: 'bg-purple-50 border-purple-200' };
          const iconColorMap = { blue: 'bg-blue-100 text-blue-600', green: 'bg-green-100 text-green-600', purple: 'bg-purple-100 text-purple-600' };
          return (
            <div key={key} className={`border rounded-xl p-4 ${colorMap?.[cfg?.color]}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColorMap?.[cfg?.color]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{cfg?.label}</p>
                  <p className="text-xs text-gray-500">{cfg?.desc}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">{count} bénéficiaire(s)</span>
                <span className={`text-xs font-semibold ${active === count && count > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  {active}/{count} actif(s)
                </span>
              </div>
              {count > 0 && (
                <div className="mt-2 h-1.5 bg-white rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${cfg?.color === 'blue' ? 'bg-blue-400' : cfg?.color === 'green' ? 'bg-green-400' : 'bg-purple-400'}`} style={{ width: `${count > 0 ? (active / count) * 100 : 0}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <select value={filterRole} onChange={e => setFilterRole(e?.target?.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent">
          <option value="all">Tous les rôles</option>
          <option value="agent">Agents</option>
          <option value="notary">Notaires</option>
          <option value="seller">Vendeurs</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent">
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="processing">En cours</option>
          <option value="released">Libéré</option>
          <option value="failed">Échoué</option>
        </select>
        <div className="flex items-center gap-2 ml-auto">
          <LivePulse />
          <span className="text-xs text-gray-500">
            {lastUpdated ? `FX mis à jour: ${new Date(lastUpdated)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : 'Taux de repli actifs'}
          </span>
        </div>
      </div>

      {/* Recipients List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800">Bénéficiaires ({filtered?.length})</h3>
          <span className="text-xs text-gray-500">Cliquer pour voir les détails FX et routage</span>
        </div>
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <RefreshCw className="w-6 h-6 text-amber-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Chargement des données en temps réel...</p>
          </div>
        ) : filtered?.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <Wallet className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Aucun bénéficiaire trouvé</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered?.map(r => (
              <RecipientRow key={r?.id} recipient={r} rates={fxRates} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimePayoutTracker;
